// frontend/src/shared/utils/helpers/fetchUtils.ts

/**
 * 🌐 FETCH UTILITIES - Request helpers con refresh automático
 *
 * Características:
 * - Auto-refresh: Intercepta 401 y refresca tokens automáticamente
 * - Auto-retry: Reintenta request original después de refresh exitoso
 * - Smart caching: Evita múltiples refreshes simultáneos
 * - Error handling: Logging detallado de errores
 *
 * Flujo de refresh:
 * 1. Request falla con 401 → Intentar refresh
 * 2. Refresh exitoso → Reintentar request original
 * 3. Refresh falla → Logout automático (vía callback)
 *
 * @see Phase 1, Task 1.2 - ROADMAP_TO_10.md (Frontend - Día 2)
 */

import { logger } from '../logger';
import { authRefreshService } from '../../services/authRefreshService';

/**
 * Configuración de retry
 */
const RETRY_CONFIG = {
	MAX_RETRIES: 1, // Solo reintentar una vez después de refresh
	RETRY_DELAY_MS: 100, // Pequeño delay antes de retry
};

/**
 * URLs que NO deben triggear refresh automático
 * (para evitar loops infinitos)
 */
const REFRESH_EXCLUDED_URLS = [
	'/api/auth/login',
	'/api/auth/logout',
	'/api/auth/refresh',
	'/api/auth/reset-password',
];

/**
 * Verifica si una URL debe ser excluida del refresh automático
 *
 * @param url - URL del request
 * @returns true si debe ser excluida
 */
function shouldExcludeFromRefresh(url: string): boolean {
	return REFRESH_EXCLUDED_URLS.some((excludedUrl) => url.includes(excludedUrl));
}

/**
 * Pequeño delay antes de retry
 *
 * @param ms - Milisegundos a esperar
 */
function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

// ============= FUNCIONES AUXILIARES PARA REDUCIR COMPLEJIDAD COGNITIVA =============

/**
 * Intenta refrescar el token y reintentar el request
 */
async function attemptTokenRefreshAndRetry(
	url: string,
	options: RequestInit,
	response: Response,
	retryCount: number
): Promise<Response> {
	logger.info('Attempting token refresh', { url });

	const refreshSuccess = await authRefreshService.refreshAccessToken();

	if (refreshSuccess) {
		logger.info('Token refreshed, retrying original request', { url });
		await delay(RETRY_CONFIG.RETRY_DELAY_MS);
		return fetchWithAuth(url, options, retryCount + 1);
	}

	logger.error('Token refresh failed, user will be logged out', { url });
	return response;
}

/**
 * Maneja respuestas 401 Unauthorized
 */
async function handleUnauthorized(
	url: string,
	options: RequestInit,
	response: Response,
	retryCount: number
): Promise<Response> {
	logger.warn('Unauthorized request detected', {
		url,
		method: options.method || 'GET',
		retryCount,
	});

	const shouldRefresh = !shouldExcludeFromRefresh(url) && retryCount === 0;

	if (shouldRefresh) {
		return attemptTokenRefreshAndRetry(url, options, response, retryCount);
	}

	return response;
}

/**
 * Registra logs para respuestas de error conocidas
 */
function logKnownErrors(url: string, options: RequestInit, response: Response): void {
	if (response.status === 403) {
		logger.warn('Forbidden request', {
			url,
			method: options.method || 'GET',
		});
	}

	if (response.status >= 500) {
		logger.error('Server error', {
			url,
			method: options.method || 'GET',
			status: response.status,
		});
	}
}

// ============= FIN FUNCIONES AUXILIARES =============

/**
 * Fetch con autenticación y refresh automático de tokens
 *
 * Comportamiento:
 * - Requests normales: Pasan directamente
 * - 401 en endpoints normales: Intenta refresh + retry
 * - 401 en endpoints de auth: No intenta refresh (evita loops)
 * - Refresh falla: Logout automático
 *
 * @param url - URL del endpoint
 * @param options - Opciones de fetch
 * @param retryCount - Contador interno de reintentos (no usar manualmente)
 * @returns Response del servidor
 */
export const fetchWithAuth = async (
	url: string,
	options: RequestInit = {},
	retryCount: number = 0
): Promise<Response> => {
	const defaultOptions: RequestInit = {
		credentials: 'include', // Siempre enviar cookies (access token)
		headers: {
			Accept: 'application/json',
			...options.headers,
		},
	};

	const fetchOptions = { ...defaultOptions, ...options };

	try {
		const response = await fetch(url, fetchOptions);

		// Manejar 401 Unauthorized con posible refresh
		if (response.status === 401) {
			return handleUnauthorized(url, options, response, retryCount);
		}

		// Registrar logs para errores conocidos (403, 5xx)
		logKnownErrors(url, options, response);

		return response;
	} catch (error) {
		logger.error('Network request failed', {
			url,
			method: options.method || 'GET',
			error: error instanceof Error ? error.message : String(error),
		});
		throw error;
	}
};
